import React from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
} from 'recharts';
import { Gamepad2, UtensilsCrossed, Palette, PawPrint, MapPin, Users, Heart } from 'lucide-react';
import { HolisticStudent } from '../../../types';

const COLORS = ['#6366F1', '#F59E0B', '#EC4899', '#8B5CF6', '#F97316', '#A855F7'];

const CARD_THEMES = [
  { bg: 'from-indigo-500 to-violet-600', icon: 'text-yellow-300' },
  { bg: 'from-rose-500 to-pink-600', icon: 'text-orange-200' },
  { bg: 'from-purple-500 to-fuchsia-600', icon: 'text-pink-200' },
  { bg: 'from-amber-400 to-orange-500', icon: 'text-white' },
];

const InterestsTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();
  const int = student.interests;

  // Build a simple pie from things they like
  const likes = int.things_i_like.split(',').map(s => s.trim()).filter(Boolean);
  const pieData = likes.map((like, i) => ({ name: like, value: 1, color: COLORS[i % COLORS.length] }));

  const livingType = int.i_live_in.toLowerCase().includes('urban') ? 'Urban' :
    int.i_live_in.toLowerCase().includes('rural') ? 'Rural' : 'Semi-Urban';

  return (
    <div className="space-y-6">
      {/* Interest Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InterestCard themeIdx={0} icon={<Gamepad2 size={22} />} label="Favourite Games" value={int.favourite_games} />
        <InterestCard themeIdx={1} icon={<UtensilsCrossed size={22} />} label="Favourite Food" value={int.favourite_food} />
        <InterestCard themeIdx={2} icon={<Palette size={22} />} label="Favourite Colour" value={int.favourite_colour} />
        <InterestCard themeIdx={3} icon={<PawPrint size={22} />} label="Favourite Animals" value={int.favourite_animals} />
      </div>

      {/* Charts & Lifestyle */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Things I Like Pie */}
        <div className="bg-white rounded-2xl border border-indigo-100 p-6 shadow-md relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/40 to-violet-50/30 pointer-events-none rounded-2xl" />
          <h3 className="relative text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-2 h-4 bg-indigo-500 rounded-full inline-block" />
            Things I Like
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                dataKey="value"
                nameKey="name"
                paddingAngle={4}
                label={({ name }) => name}
              >
                {pieData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Lifestyle Indicators */}
        <div className="bg-white rounded-2xl border border-indigo-100 p-6 shadow-md relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 to-cyan-50/30 pointer-events-none rounded-2xl" />
          <h3 className="relative text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-2 h-4 bg-blue-500 rounded-full inline-block" />
            Lifestyle Indicators
          </h3>
          <div className="space-y-4">
            <LifestyleRow
              icon={<MapPin size={16} className="text-blue-500" />}
              label="Living Area"
              value={int.i_live_in}
              badge={livingType}
              badgeColor={livingType === 'Urban' ? 'bg-blue-50 text-blue-700' : livingType === 'Rural' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}
            />
            <LifestyleRow
              icon={<Users size={16} className="text-indigo-500" />}
              label="Friends"
              value={int.friends_are}
            />
            <LifestyleRow
              icon={<Heart size={16} className="text-rose-500" />}
              label="Things I Like"
              value={int.things_i_like}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const InterestCard: React.FC<{ icon: React.ReactNode; label: string; value: string; themeIdx: number }> = ({ icon, label, value, themeIdx }) => {
  const t = CARD_THEMES[themeIdx % CARD_THEMES.length];
  return (
    <div className={`bg-gradient-to-br ${t.bg} rounded-2xl p-5 shadow-lg text-center`}>
      <div className={`flex justify-center mb-3 ${t.icon}`}>{icon}</div>
      <p className="text-white/70 text-xs mb-1 font-medium">{label}</p>
      <p className="text-white font-black text-base tracking-tight">{value}</p>
    </div>
  );
};

const LifestyleRow: React.FC<{ icon: React.ReactNode; label: string; value: string; badge?: string; badgeColor?: string }> = ({ icon, label, value, badge, badgeColor }) => (
  <div className="flex items-start gap-3 p-3.5 bg-gradient-to-r from-gray-50 to-indigo-50/30 rounded-xl border border-indigo-50 hover:border-indigo-100 transition">
    <div className="mt-0.5 p-1.5 rounded-lg bg-white shadow-sm">{icon}</div>
    <div className="flex-1">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className="text-sm text-gray-800 font-medium mt-0.5">{value}</p>
    </div>
    {badge && (
      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${badgeColor}`}>{badge}</span>
    )}
  </div>
);

export default InterestsTab;
